// src/components/admin-tools/NewToolModal.tsx
//
// Modal "Nova tool" — wireframe §13 (dlgNewTool): nome · slug (snake_case,
// auto-derivado do nome) · categoria · descrição · atributos · "Ativa ao criar
// (Não=pending_deploy / Sim)". SEM campo Runtime (nota v18 — não reintroduzir).
//
// Fluxo real (backend verificado em raios src/admin/tools.routes.ts):
//   1. POST /v1/admin/tools { slug, name, scope_text, category, is_mutating,
//      requires_admin } — SÍNCRONO: invoca Mario (LLM) → artifacts → INSERT
//      pending_deploy → abre PR. Pode demorar minutos.
//   2. Atributos NÃO aceitos no POST (is_core / mcp_exposed / is_companion_only)
//      e "Ativa ao criar = Sim" (is_active) são aplicados num PATCH
//      /v1/admin/tools/:slug logo após o create (a row já existe).

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { CheckCircle, Loader2, Plus } from 'lucide-react';
import { AttributeChip } from './AttributeChip';
import { createAdminTool, type CreateToolResult } from './tools-api';
import { CREATABLE_CATEGORIES, deriveSlug, SLUG_RE } from './tools-helpers';

interface AttrState {
  is_core: boolean;
  requires_admin: boolean;
  mcp_exposed: boolean;
  is_companion_only: boolean;
}

// Defaults do wireframe: core ON · admin-only OFF · mcp OFF · companion-only OFF.
const DEFAULT_ATTRS: AttrState = {
  is_core: true,
  requires_admin: false,
  mcp_exposed: false,
  is_companion_only: false,
};

export function NewToolModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Chamado após criar (e aplicar atributos) — recarrega a lista na página. */
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [category, setCategory] = useState<string>('dispatch');
  const [description, setDescription] = useState('');
  const [attrs, setAttrs] = useState<AttrState>(DEFAULT_ATTRS);
  const [activateOnCreate, setActivateOnCreate] = useState<'nao' | 'sim'>('nao');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateToolResult | null>(null);
  const [patchWarning, setPatchWarning] = useState<string | null>(null);

  // Reset ao abrir.
  useEffect(() => {
    if (open) {
      setName('');
      setSlug('');
      setSlugTouched(false);
      setCategory('dispatch');
      setDescription('');
      setAttrs(DEFAULT_ATTRS);
      setActivateOnCreate('nao');
      setSubmitting(false);
      setError(null);
      setCreated(null);
      setPatchWarning(null);
    }
  }, [open]);

  // Slug auto-derivado do nome enquanto o usuário não editar o slug à mão.
  function onNameChange(v: string) {
    setName(v);
    if (!slugTouched) setSlug(deriveSlug(v));
  }

  const slugValid = SLUG_RE.test(slug);
  const canSubmit = !submitting && name.trim().length > 0 && slugValid && description.trim().length > 0;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setPatchWarning(null);
    try {
      const result = await createAdminTool({
        slug,
        name: name.trim(),
        scope_text: description.trim(),
        category: category as (typeof CREATABLE_CATEGORIES)[number],
        // query é leitura; demais categorias mutam (default do backend é true)
        is_mutating: category !== 'query',
        requires_admin: attrs.requires_admin,
      });

      // Atributos fora do contrato do POST → PATCH na row recém-criada.
      const flags: Record<string, boolean> = {};
      if (attrs.is_core) flags['is_core'] = true;
      if (attrs.mcp_exposed) flags['mcp_exposed'] = true;
      if (attrs.is_companion_only) flags['is_companion_only'] = true;
      if (activateOnCreate === 'sim') flags['is_active'] = true;
      if (Object.keys(flags).length > 0) {
        try {
          await api.patchAdminToolFlags(result.slug, flags);
        } catch (patchErr) {
          setPatchWarning(
            `Tool criada, mas falhou ao aplicar atributos (${patchErr instanceof Error ? patchErr.message : 'erro'}) — ajuste pelos chips do card.`,
          );
        }
      }

      setCreated(result);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao criar tool');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-lg w-[95vw] max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="size-4 text-raios-accent" /> Nova tool
          </DialogTitle>
          <DialogDescription>
            A tool nasce em <code>pending_deploy</code>: o Mario gera o handler, registra no core
            e abre um PR — após merge + deploy ela ativa.
          </DialogDescription>
        </DialogHeader>

        {created ? (
          // Sucesso — mostra status real + PR (criação é assíncrona até o deploy).
          <div className="space-y-3">
            <div className="rounded-lg border border-raios-success/40 bg-raios-success/10 p-3 text-sm text-raios-text flex items-start gap-2.5">
              <CheckCircle className="size-4 text-raios-success shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p>
                  Tool <code className="font-semibold">{created.slug}</code> criada em status{' '}
                  <code>{created.status}</code>.
                </p>
                {created.pr_url && (
                  <p className="mt-1">
                    PR{created.pr_number ? ` #${created.pr_number}` : ''}:{' '}
                    <a
                      href={created.pr_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-raios-accent-text underline underline-offset-2 break-all"
                    >
                      {created.pr_url}
                    </a>
                  </p>
                )}
                <p className="mt-1 text-xs text-raios-muted">
                  Após merge + deploy, o listener marca a tool como ativa.
                </p>
              </div>
            </div>
            {patchWarning && (
              <div className="rounded-md border border-raios-warning/40 bg-raios-warning/10 p-2 text-xs text-raios-warning-text">
                {patchWarning}
              </div>
            )}
            <DialogFooter>
              <Button variant="brand" onClick={onClose}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nt-name">Nome da tool</Label>
              <Input
                id="nt-name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="ex: Enviar fatura"
                disabled={submitting}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="nt-slug">Slug (snake_case)</Label>
                <Input
                  id="nt-slug"
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value);
                  }}
                  placeholder="ex: send_invoice"
                  disabled={submitting}
                />
                {slug.length > 0 && !slugValid && (
                  <p className="text-xs text-raios-error">
                    Slug deve ser snake_case (letra minúscula no início; só a-z, 0-9 e _).
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select
                  options={CREATABLE_CATEGORIES.map((c) => ({ value: c, label: c }))}
                  value={category}
                  onChange={setCategory}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nt-desc">Descrição (escopo do que a tool faz)</Label>
              <Textarea
                id="nt-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="O que a tool faz, quando usar, o que ela muta"
                className="min-h-[84px]"
                disabled={submitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Atributos</Label>
              {/* Chips literais do wireframe (.tool-chips .chip ok/k/off) */}
              <div className="tool-chips" style={{ marginTop: 2 }}>
                <AttributeChip
                  active={attrs.is_core}
                  onToggle={() => setAttrs((a) => ({ ...a, is_core: !a.is_core }))}
                  label="core"
                  tone="ok"
                />
                <AttributeChip
                  active={attrs.requires_admin}
                  onToggle={() => setAttrs((a) => ({ ...a, requires_admin: !a.requires_admin }))}
                  label="admin-only"
                  tone="k"
                />
                <AttributeChip
                  active={attrs.mcp_exposed}
                  onToggle={() => setAttrs((a) => ({ ...a, mcp_exposed: !a.mcp_exposed }))}
                  label="mcp"
                />
                <AttributeChip
                  active={attrs.is_companion_only}
                  onToggle={() =>
                    setAttrs((a) => ({ ...a, is_companion_only: !a.is_companion_only }))
                  }
                  label="companion-only"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Ativa ao criar</Label>
              <Select
                options={[
                  { value: 'nao', label: 'Não (pending_deploy)' },
                  { value: 'sim', label: 'Sim' },
                ]}
                value={activateOnCreate}
                onChange={(v) => setActivateOnCreate(v as 'nao' | 'sim')}
                disabled={submitting}
              />
              {activateOnCreate === 'sim' && (
                <p className="text-xs text-raios-warning-text">
                  Atenção: o código da tool só existe após merge + deploy do PR — ativar antes
                  liga a flag, mas a execução depende do deploy.
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-md border border-raios-error/40 bg-raios-error/10 p-2 text-xs text-raios-error">
                {error}
              </div>
            )}

            {submitting && (
              <p className="text-xs text-raios-muted">
                Gerando a tool (Mario escreve o handler e abre o PR) — isso pode levar alguns
                minutos. Não feche esta janela.
              </p>
            )}

            <DialogFooter>
              <Button variant="ghost" disabled={submitting} onClick={onClose}>
                Cancelar
              </Button>
              <Button variant="brand" disabled={!canSubmit} onClick={() => void submit()}>
                {submitting && <Loader2 className="size-4 animate-spin" />}
                {submitting ? 'Criando…' : 'Criar tool'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
